import React, { useEffect, useState } from 'react';

import { Alert, Box, LinearProgress, Typography } from '@mui/material';

import { type PersonalViewState, useGetPersonalViews } from '~/hooks/apis/reporting/usePersonalViews';
import useCurrentUser from '~/hooks/useCurrentUser';

import LegacyViewsImport from './LegacyViewsImport';
import PersonalViews from './PersonalViews';
import {
  VIEW_REPORT_VERSION_PARAM,
  type ViewKind,
  captureViewState,
  exactViewHref,
  hasExplicitView,
  personalViewHref,
} from './viewState';

type Decision = { href?: string; warning?: string };

/** Resolve the personal default before mounting a report, whose existing codec reads the URL. */
export default function PersonalViewBoundary({
  lensId,
  reportVersion,
  kind = 'lens',
  children,
}: {
  lensId: string;
  reportVersion: number;
  kind?: ViewKind;
  children: React.ReactNode;
}) {
  const { user } = useCurrentUser();
  const views = useGetPersonalViews(lensId);
  const currentReportVersion = Math.max(reportVersion, views.data?.reportVersion ?? 0);
  const [entry] = useState(() => window.location.search);
  const [decision, setDecision] = useState<Decision | null>(() => (hasExplicitView(kind, entry) ? {} : null));
  useEffect(() => {
    if (decision !== null) return;
    if (!user?.email || views.isError) {
      setDecision({ warning: 'Your saved views could not be loaded. Showing the published default.' });
      return;
    }
    if (!views.isSuccess) return;
    const preferred = views.data.views.find((view) => view.isDefault && !view.deletedAt);
    if (!preferred) {
      setDecision({});
      return;
    }
    if (preferred.reportChanged || preferred.reportVersion !== currentReportVersion) {
      setDecision({ warning: 'This report changed since your default was saved. Open Saved views to review it.' });
      return;
    }
    try {
      setDecision({ href: personalViewHref(kind, preferred.state, window.location) });
    } catch (error) {
      setDecision({ warning: error instanceof Error ? error.message : 'Your default could not be restored.' });
    }
  }, [decision, entry, kind, currentReportVersion, user?.email, views.data, views.isError, views.isSuccess]);
  useEffect(() => {
    if (decision?.href) window.location.replace(decision.href);
  }, [decision]);
  if (!decision || decision.href) {
    return (
      <Box role="status" sx={{ px: 2, py: 2 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading your report view…
        </Typography>
      </Box>
    );
  }
  const open = (state: PersonalViewState) => window.location.assign(personalViewHref(kind, state, window.location));
  const linkVersion = new URLSearchParams(entry).get(VIEW_REPORT_VERSION_PARAM);
  const linkWarning =
    linkVersion && linkVersion !== String(currentReportVersion)
      ? 'This report changed since this link was copied. Check the filters and dates before using the results.'
      : undefined;
  return (
    <>
      <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1, flexShrink: 0 }}>
        {(decision.warning || linkWarning) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {decision.warning || linkWarning}
          </Alert>
        )}
        <PersonalViews
          key={`views:${user?.email ?? 'signed-out'}`}
          lensId={lensId}
          reportVersion={reportVersion}
          state={() => captureViewState(kind, window.location.search, window.location.pathname)}
          onOpen={open}
          onReset={() => open({ version: 1, search: '' })}
          onCopy={() => navigator.clipboard.writeText(exactViewHref(kind, window.location, currentReportVersion))}
        />
        {kind === 'native' && window.location.pathname === '/commercial-ops/contribution-margin' && (
          <LegacyViewsImport key={`import:${user?.email ?? 'signed-out'}`} lensId={lensId} />
        )}
      </Box>
      {children}
    </>
  );
}
